import { useEffect, useState, useCallback } from "react";
import { appendUnique, firstPage, listedRows, nextPage, pickerPage, trimPage } from "../../lib/paging";
import { LoadMore } from "./LoadMore";
import { Link } from "react-router";
import { Card, Button, Input, Stack, EmptyState, useToast } from "@salesapp/ui";
import { crmApi, type Company, type Contact } from "../../lib/crmApi";
import { reportSaveFailure } from "../../lib/saveNotice";
import { FIELD_HINTS } from "../../help/fieldHints";
import { CompanyLookup } from "./CompanyLookup";
import { ListWorkspace, DeleteRecordButton } from "./RecordWorkspace";
import { ResponsiveActions } from "./ResponsiveActions";
import { SearchBar } from "./SearchBar";
import { ContactDuplicateWarning, RecordAuditInfo, useContactDuplicates } from "./RecordAuditInfo";
import { useCrmHref } from "../../lib/customerUrl";
import { ContactViewCard, contactName } from "./ContactViewCard";
import { useLoxleyOffer } from "../../loxley/LoxleyProvider";

export function ContactsPage({ orgId }: { orgId: string }) {
  const crmHref = useCrmHref();
  const { show } = useToast();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    work_email: "",
    personal_email: "",
    mobile: "",
    gender: "",
    company_id: "",
    company_name: "",
    marketing_enabled: false,
    is_active: true,
  });

  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const editingContact = contacts.find((person) => person.id === editingId) ?? null;
  const [viewingId, setViewingId] = useState<string | null>(null);
  const viewingContact = contacts.find((person) => person.id === viewingId) ?? null;
  useLoxleyOffer(viewingContact ? { kind: "contact", id: viewingContact.id, label: contactName(viewingContact) } : null);
  const contactDuplicates = useContactDuplicates(
    orgId,
    { company_id: form.company_id, email: form.work_email, first_name: form.first_name, last_name: form.last_name, mobile: form.mobile },
    editingId,
  );

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const listed = listedRows(await crmApi.listContacts(orgId, undefined, search || undefined, nextPage(contacts.length), { withTotal: true }));
      const page = trimPage(listed.rows);
      setContacts((current) => appendUnique(current, page.items));
      setHasMore(page.hasMore);
      setTotal(listed.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load more contacts");
    } finally {
      setLoadingMore(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [people, orgs] = await Promise.all([
        crmApi.listContacts(orgId, undefined, search || undefined, firstPage(), { withTotal: true }),
        crmApi.listCompanies(orgId, undefined, pickerPage()),
      ]);
      const listed = listedRows(people);
      const page = trimPage(listed.rows);
      setContacts(page.items);
      setHasMore(page.hasMore);
      setTotal(listed.total);
      setCompanies(orgs);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, [orgId, search]);

  useEffect(() => {
    void load();
  }, [load]);

  const companyName = (id: string) => companies.find((item) => item.id === id)?.legal_name ?? id.slice(0, 8);

  const openEdit = (c: Contact) => {
    setViewingId(null);
    setEditingId(c.id);
    setShowForm(true);
    setForm({
      first_name: c.first_name,
      last_name: c.last_name ?? "",
      work_email: c.work_email ?? c.email ?? "",
      personal_email: c.personal_email ?? "",
      mobile: c.mobile ?? "",
      gender: c.gender ?? "",
      company_id: c.company_id,
      company_name: c.company_name ?? companyName(c.company_id),
      marketing_enabled: Boolean(c.marketing_enabled),
      is_active: c.is_active !== false,
    });
  };

  return (
    <ListWorkspace
      title="Contacts"
      actions={
        <Button onClick={() => { setViewingId(null); setShowForm((value) => !value); }}>
          {showForm ? "Cancel" : "New contact"}
        </Button>
      }
      toolbar={
        <div className="crm-toolbar">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search contacts…"
            label="Search contacts"
            hint="Search by name, email, phone, or company."
          />
        </div>
      }
    >
      {error && <Card className="crm-error">{error}</Card>}
      {viewingContact && !showForm ? (
        <ContactViewCard
          person={viewingContact}
          showCompany
          onEdit={() => openEdit(viewingContact)}
          onClose={() => setViewingId(null)}
        />
      ) : null}
      {showForm && (
        <Card>
          <Stack gap={3}>
            <CompanyLookup
              orgId={orgId}
              value={form.company_name}
              companyId={form.company_id}
              onChange={({ name, companyId }) => setForm({ ...form, company_name: name, company_id: companyId })}
            />
            <Input label="First name *" tooltip={FIELD_HINTS.contact_first_name} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            <Input label="Last name" tooltip={FIELD_HINTS.contact_last_name} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            <Input label="Gender" tooltip={FIELD_HINTS.contact_gender} value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} />
            <Input label="Work email" tooltip={FIELD_HINTS.contact_work_email} value={form.work_email} onChange={(e) => setForm({ ...form, work_email: e.target.value })} />
            <Input label="Personal email" tooltip={FIELD_HINTS.contact_personal_email} value={form.personal_email} onChange={(e) => setForm({ ...form, personal_email: e.target.value })} />
            <Input label="Mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
            <ContactDuplicateWarning duplicates={contactDuplicates} />
            <label className="crm-check">
              <input type="checkbox" checked={form.marketing_enabled} onChange={(e) => setForm({ ...form, marketing_enabled: e.target.checked })} />
              Marketing enabled
            </label>
            <label className="crm-check">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Active at this company
            </label>
            {editingContact ? <RecordAuditInfo record={editingContact} /> : null}
            <div className="crm-form-actions">
            <Button
              disabled={!form.first_name.trim() || !form.company_id}
              onClick={() => {
                const body = {
                  company_id: form.company_id,
                  first_name: form.first_name,
                  last_name: form.last_name || null,
                  gender: form.gender || null,
                  work_email: form.work_email || null,
                  personal_email: form.personal_email || null,
                  mobile: form.mobile || null,
                  marketing_enabled: form.marketing_enabled,
                  is_active: form.is_active,
                };
                const request = editingId
                  ? crmApi.updateContact(editingId, body, orgId)
                  : crmApi.createContact(body, orgId);
                void request
                  .then(() => {
                    setShowForm(false);
                    setEditingId(null);
                    show("Saved!", "success");
                    return load();
                  })
                  .catch((e) => setError(reportSaveFailure(show, e, "Failed to save contact")));
              }}
            >
              {editingId ? "Save contact" : "Create contact"}
            </Button>
            </div>
          </Stack>
        </Card>
      )}
      {loading ? (
        <Card>Loading...</Card>
      ) : contacts.length === 0 ? (
        <Card>
          <EmptyState title="No contacts found" description="Add people from a company record or here." />
        </Card>
      ) : (
        <Card className="crm-table-card">
          <table className="crm-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Work email</th>
                <th>Company</th>
                <th>Marketing</th>
                <th>Status</th>
                <th className="crm-table-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id}>
                  <td>
                    {c.first_name} {c.last_name}
                    {c.is_primary && <span className="crm-badge crm-badge--active">Primary</span>}
                  </td>
                  <td>{c.work_email ?? c.email ?? "—"}</td>
                  <td>
                    <Link to={crmHref(`/companies/${c.company_id}`)}>{c.company_name ?? companyName(c.company_id)}</Link>
                  </td>
                  <td>{c.marketing_enabled ? "Yes" : "No"}</td>
                  <td>
                    <span className={`crm-badge ${c.is_active === false ? "crm-badge--lost" : "crm-badge--active"}`}>
                      {c.is_active === false ? "Inactive" : "Active"}
                    </span>
                  </td>
                  <td className="crm-table-actions">
                    <ResponsiveActions
                      primary={
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`View ${contactName(c)}`}
                          title="Open this contact to read its details. Nothing can change until you click Edit."
                          onClick={() => {
                            setShowForm(false);
                            setEditingId(null);
                            setViewingId(c.id);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                        >
                          View
                        </Button>
                      }
                      more={
                        <>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(c)}>
                            Edit
                          </Button>
                          <DeleteRecordButton
                            capability="crm.contacts"
                            recordLabel={contactName(c)}
                            size="sm"
                            variant="ghost"
                            onDeleted={async () => {
                              await crmApi.deleteContact(c.id, orgId);
                              await load();
                            }}
                          />
                        </>
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <LoadMore shown={contacts.length} total={total} hasMore={hasMore} loading={loadingMore} onLoadMore={() => void loadMore()} noun="contacts" />
    </ListWorkspace>
  );
}
